import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
    FaSpinner, FaTools, FaCheckCircle, FaBan, FaHourglassHalf,
    FaPlayCircle, FaStopCircle, FaChevronLeft, FaCalendarAlt,
    FaFileMedicalAlt, FaExclamationTriangle, FaUndo, FaClipboardCheck,
    FaLink, FaUnlink, FaUser, FaClock, FaShieldAlt, FaSearch,
    FaTimes, FaInfoCircle
} from 'react-icons/fa';
import './GmudDetailPage.css';

const STATUS_CONFIG = {
    Pendente:      { color: 'var(--gmud-status-pending)',   bg: 'var(--gmud-status-pending-bg)',   icon: FaHourglassHalf, label: 'Pendente de Aprovação', step: 0 },
    Aprovada:      { color: 'var(--gmud-status-approved)',  bg: 'var(--gmud-status-approved-bg)',  icon: FaCheckCircle,   label: 'Aprovada',              step: 1 },
    'Em Execução': { color: 'var(--gmud-status-running)',   bg: 'var(--gmud-status-running-bg)',   icon: FaPlayCircle,    label: 'Em Execução',           step: 2 },
    Concluída:     { color: 'var(--gmud-status-done)',      bg: 'var(--gmud-status-done-bg)',      icon: FaCheckCircle,   label: 'Concluída',             step: 3 },
    Rejeitada:     { color: 'var(--gmud-status-rejected)',  bg: 'var(--gmud-status-rejected-bg)',  icon: FaBan,           label: 'Rejeitada',             step: -1 },
    Cancelada:     { color: 'var(--gmud-status-cancelled)', bg: 'var(--gmud-status-cancelled-bg)', icon: FaStopCircle,    label: 'Cancelada',             step: -1 },
};

const LIFECYCLE_STEPS = ['Pendente', 'Aprovada', 'Em Execução', 'Concluída'];

const formatDate = (d) => d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const formatDateShort = (d) => d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

/* ── Lifecycle Bar ─────────────────────────────────────────── */
const LifecycleBar = ({ status }) => {
    const cfg = STATUS_CONFIG[status];
    const currentStep = cfg?.step ?? 0;
    const isTerminal = currentStep === -1;

    return (
        <div className="gmud-lifecycle-container">
            {isTerminal ? (
                <div className="gmud-lifecycle-terminal">
                    <cfg.icon size={16} />
                    <span>Esta GMUD foi <strong>{cfg.label}</strong> e está fora do fluxo normal de execução.</span>
                </div>
            ) : (
                <div className="gmud-lifecycle-track">
                    {LIFECYCLE_STEPS.map((step, idx) => {
                        const sCfg = STATUS_CONFIG[step];
                        const Icon = sCfg.icon;
                        const isDone    = idx < currentStep;
                        const isCurrent = idx === currentStep;
                        
                        return (
                            <React.Fragment key={step}>
                                <div className={`gmud-lc-step ${isDone ? 'is-done' : ''} ${isCurrent ? 'is-current' : ''}`}>
                                    <div 
                                        className="gmud-lc-indicator" 
                                        style={isCurrent ? { borderColor: sCfg.color, backgroundColor: sCfg.bg, color: sCfg.color } : {}}
                                    >
                                        <Icon size={12} className="gmud-lc-icon" />
                                    </div>
                                    <span className="gmud-lc-label">{step}</span>
                                </div>
                                {idx < LIFECYCLE_STEPS.length - 1 && (
                                    <div className={`gmud-lc-connector ${isDone ? 'is-done' : ''}`} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/* ── Vincular Chamado Modal ────────────────────────────────── */
const VincularModal = ({ gmudId, onClose, onAdded }) => {
    const [query, setQuery]       = useState('');
    const [results, setResults]   = useState([]);
    const [searching, setSearching] = useState(false);
    const [linking, setLinking]   = useState(null);

    const search = async (q) => {
        setQuery(q);
        if (!q.trim()) { setResults([]); return; }
        setSearching(true);
        try {
            const { data } = await api.get(`/api/gmud/busca/chamados-para-vincular?q=${encodeURIComponent(q)}`);
            setResults(data);
        } catch { toast.error('Erro ao buscar chamados.'); }
        finally { setSearching(false); }
    };

    const vincular = async (chamadoId) => {
        setLinking(chamadoId);
        try {
            await api.post(`/api/gmud/${gmudId}/vincular-chamado`, { chamado_id: chamadoId });
            onAdded();
            onClose();
        } catch {
            toast.error('Falha ao vincular chamado.');
        } finally {
            setLinking(null);
        }
    };

    return (
        <div className="gmud-modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="gmud-modal-box">
                <div className="gmud-modal-header">
                    <h3 className="gmud-modal-title"><FaLink className="gmud-icon-muted" /> Vincular Incidente</h3>
                    <button onClick={onClose} className="gmud-modal-close-btn"><FaTimes /></button>
                </div>
                <div className="gmud-modal-body">
                    <p className="gmud-modal-subtitle">Busque incidentes abertos para associar a esta GMUD.</p>
                    
                    <div className="gmud-search-wrapper">
                        <FaSearch size={14} className="gmud-search-icon" />
                        <input
                            autoFocus
                            type="text"
                            placeholder="Digite o título ou ID do chamado..."
                            value={query}
                            onChange={e => search(e.target.value)}
                            className="gmud-input gmud-modal-search-input"
                        />
                        {searching && <FaSpinner className="gmud-spinner gmud-search-spinner" size={14} />}
                    </div>

                    <div className="gmud-modal-results">
                        {results.length > 0 ? results.map(c => (
                            <div key={c.id} className="gmud-result-card">
                                <div className="gmud-result-info">
                                    <span className="gmud-tag-id">#{c.id}</span>
                                    <span className="gmud-result-text">{c.titulo}</span>
                                </div>
                                <button
                                    className="gmud-btn gmud-btn-primary gmud-btn-sm"
                                    onClick={() => vincular(c.id)}
                                    disabled={!!linking}
                                >
                                    {linking === c.id ? <FaSpinner className="gmud-spinner" size={12} /> : 'Vincular'}
                                </button>
                            </div>
                        )) : query && !searching ? (
                            <div className="gmud-empty-state-sm">
                                <FaInfoCircle size={20} className="gmud-icon-muted" />
                                <p>Nenhum incidente aberto encontrado.</p>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ── Section Card ──────────────────────────────────────────── */
const Card = ({ title, icon: Icon, children, fullWidth, className = '' }) => (
    <div className={`gmud-card ${fullWidth ? 'gmud-card-full' : ''} ${className}`}>
        {title && (
            <div className="gmud-card-header">
                {Icon && <Icon className="gmud-card-icon" />}
                <h4 className="gmud-card-title">{title}</h4>
            </div>
        )}
        <div className="gmud-card-body">
            {children}
        </div>
    </div>
);

/* ── Field Row ─────────────────────────────────────────────── */
const Field = ({ label, value, mono }) => (
    <div className="gmud-data-field">
        <span className="gmud-data-label">{label}</span>
        <span className={`gmud-data-value ${mono ? 'gmud-font-mono' : ''}`}>{value || '—'}</span>
    </div>
);

/* ─────────────────────────────────────────────────────────────
   GmudDetailPage
   ───────────────────────────────────────────────────────────── */
const GmudDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [gmud, setGmud]           = useState(null);
    const [loading, setLoading]     = useState(true);
    const [resultado, setResultado] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [saving, setSaving]       = useState(false);

    const fetchGmud = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/api/gmud/${id}`);
            data.chamados_vinculados = data.chamados_vinculados || [];
            setGmud(data);
        } catch {
            toast.error('Não foi possível carregar os detalhes da GMUD.');
            navigate('/suporte-admin/gmud');
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => { fetchGmud(); }, [fetchGmud]);

    const handleUpdateStatus = async (newStatus) => {
        if (!window.confirm(`Confirmar ação: "${newStatus}" esta GMUD?`)) return;
        setSaving(true);
        try {
            await api.put(`/api/gmud/${id}/status`, { status: newStatus });
            toast.success(`GMUD atualizada para "${newStatus}" com sucesso!`);
            fetchGmud();
        } catch {
            toast.error('Falha ao atualizar o status da GMUD.');
        } finally {
            setSaving(false);
        }
    };

    const handleSalvarResultado = async (e) => {
        e.preventDefault();
        if (!resultado.trim()) { toast.warn('Descreva o resultado da execução antes de salvar.'); return; }
        setSaving(true);
        try {
            await api.post(`/api/gmud/${id}/finalizar`, { resultado });
            toast.success('GMUD finalizada com sucesso!');
            fetchGmud();
            setResultado('');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Falha ao salvar o resultado.');
        } finally {
            setSaving(false);
        }
    };

    const handleDesvincular = async (chamadoId) => {
        if (!window.confirm(`Desvincular chamado #${chamadoId}?`)) return;
        try {
            await api.delete(`/api/gmud/${id}/vincular-chamado/${chamadoId}`);
            toast.success('Vínculo removido.');
            fetchGmud();
        } catch {
            toast.error('Falha ao remover vínculo.');
        }
    };

    if (loading || !gmud) {
        return (
            <div className="gmud-page-loader">
                <FaSpinner className="gmud-spinner" size={24} />
                <span>Carregando detalhes da GMUD...</span>
            </div>
        );
    }

    const statusInfo = STATUS_CONFIG[gmud.status] || STATUS_CONFIG.Pendente;
    const StatusIcon = statusInfo.icon;
    const canFinalize = ['Aprovada', 'Em Execução'].includes(gmud.status);

    return (
        <div className="gmud-page-container">
            {/* ── Top Bar ── */}
            <div className="gmud-page-topbar">
                <button className="gmud-btn gmud-btn-ghost gmud-back-link" onClick={() => navigate('/suporte-admin/gmud')}>
                    <FaChevronLeft size={12} /> Voltar para Gestão de Mudanças
                </button>
                <div className="gmud-topbar-actions">
                    <span 
                        className="gmud-badge gmud-badge-status" 
                        style={{ color: statusInfo.color, background: statusInfo.bg }}
                    >
                        <StatusIcon size={12} className="gmud-badge-icon" />
                        {statusInfo.label}
                    </span>
                    <span className="gmud-tag-id gmud-tag-id-large">GMUD #{gmud.id}</span>
                </div>
            </div>

            {/* ── Title ── */}
            <div className="gmud-header-block">
                <h1 className="gmud-main-title">{gmud.titulo}</h1>
                {gmud.tipo && <span className="gmud-badge gmud-badge-tipo">{gmud.tipo}</span>}
            </div>

            {/* ── Lifecycle ── */}
            <LifecycleBar status={gmud.status} />

            {/* ── Main Grid ── */}
            <div className="gmud-content-grid">

                {/* Col A: Main info */}
                <div className="gmud-grid-col-main">
                    <Card title="Descrição da Mudança" icon={FaFileMedicalAlt}>
                        <p className="gmud-prose-text">{gmud.descricao || 'Nenhuma descrição fornecida.'}</p>
                    </Card>

                    <Card title="Justificativa e Impacto" icon={FaExclamationTriangle}>
                        <div className="gmud-fields-group">
                            <Field label="Justificativa" value={gmud.justificativa} />
                            <Field label="Impacto Esperado" value={gmud.impacto} />
                        </div>
                    </Card>

                    <Card title="Plano de Reversão (Rollback)" icon={FaUndo}>
                        <p className="gmud-prose-text">{gmud.plano_rollback || 'Nenhum plano de reversão registrado.'}</p>
                    </Card>

                    {/* Chamados vinculados */}
                    <Card title="Incidentes Vinculados" icon={FaLink}>
                        {gmud.chamados_vinculados.length > 0 ? (
                            <div className="gmud-linked-list">
                                {gmud.chamados_vinculados.map(c => (
                                    <div key={c.id} className="gmud-linked-item">
                                        <div className="gmud-linked-info">
                                            <span className="gmud-tag-id">#{c.id}</span>
                                            <span className="gmud-linked-title" title={c.titulo}>{c.titulo}</span>
                                        </div>
                                        <button
                                            className="gmud-btn gmud-btn-ghost-danger gmud-btn-sm"
                                            onClick={() => handleDesvincular(c.id)}
                                            title="Desvincular incidente"
                                        >
                                            <FaUnlink size={12} /> Desvincular
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="gmud-empty-text">Nenhum incidente vinculado a esta GMUD.</p>
                        )}
                        <button className="gmud-btn gmud-btn-outline gmud-mt-3" onClick={() => setIsModalOpen(true)}>
                            <FaLink size={12} /> Adicionar vínculo
                        </button>
                    </Card>

                    {/* Resultado */}
                    {(canFinalize || gmud.resultado_execucao) && (
                        <Card 
                            title="Resultado da Execução" 
                            icon={FaClipboardCheck} 
                            className={gmud.resultado_execucao ? 'gmud-card-success' : ''}
                        >
                            {gmud.resultado_execucao ? (
                                <div className="gmud-result-container">
                                    <p className="gmud-result-text-block">{gmud.resultado_execucao}</p>
                                    <div className="gmud-result-metadata">
                                        <span className="gmud-meta-item"><FaUser size={12} /> {gmud.executado_por_nome || 'N/A'}</span>
                                        <span className="gmud-meta-item"><FaClock size={12} /> {formatDate(gmud.data_real_conclusao)}</span>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={handleSalvarResultado} className="gmud-form-container">
                                    <label className="gmud-data-label">
                                        Descreva o resultado desta mudança (sucesso, falhas, observações):
                                    </label>
                                    <textarea
                                        rows={4}
                                        value={resultado}
                                        onChange={e => setResultado(e.target.value)}
                                        placeholder="Ex: Atualização aplicada com sucesso em todos os nós. Sem impacto no serviço."
                                        disabled={saving}
                                        className="gmud-textarea"
                                    />
                                    <button type="submit" className="gmud-btn gmud-btn-primary" disabled={saving}>
                                        {saving
                                            ? <><FaSpinner className="gmud-spinner" size={14} /> Salvando...</>
                                            : <><FaClipboardCheck size={14} /> Salvar e Concluir GMUD</>
                                        }
                                    </button>
                                </form>
                            )}
                        </Card>
                    )}
                </div>

                {/* Col B: Sidebar */}
                <div className="gmud-grid-col-sidebar">
                    
                    {/* Ações (Aparece no topo da sidebar se pendente) */}
                    {gmud.status === 'Pendente' && (
                        <Card title="Ações de Governança" icon={FaShieldAlt} className="gmud-card-warning">
                            <p className="gmud-prose-text gmud-text-sm gmud-mb-3">
                                Esta GMUD requer aprovação técnica ou gerencial para seguir ao agendamento.
                            </p>
                            <div className="gmud-action-stack">
                                <button
                                    className="gmud-btn gmud-btn-success gmud-btn-block"
                                    onClick={() => handleUpdateStatus('Aprovada')}
                                    disabled={saving}
                                >
                                    <FaCheckCircle size={14} /> Aprovar Mudança
                                </button>
                                <button
                                    className="gmud-btn gmud-btn-danger gmud-btn-block"
                                    onClick={() => handleUpdateStatus('Rejeitada')}
                                    disabled={saving}
                                >
                                    <FaBan size={14} /> Rejeitar Mudança
                                </button>
                            </div>
                        </Card>
                    )}

                    {/* Janela */}
                    <Card title="Janela de Execução" icon={FaCalendarAlt}>
                        <div className="gmud-window-details">
                            <div className="gmud-window-item">
                                <span className="gmud-data-label">Início Planejado</span>
                                <span className="gmud-window-value">{formatDate(gmud.janela_inicio)}</span>
                            </div>
                            <div className="gmud-divider" />
                            <div className="gmud-window-item">
                                <span className="gmud-data-label">Fim Planejado</span>
                                <span className="gmud-window-value">{formatDate(gmud.janela_fim)}</span>
                            </div>
                        </div>
                    </Card>

                    {/* Pessoas */}
                    <Card title="Envolvidos" icon={FaUser}>
                        <div className="gmud-people-list">
                            <PersonRow label="Solicitante" name={gmud.solicitante_nome} date={gmud.criado_em} />
                            {gmud.aprovador_nome && (
                                <PersonRow label={gmud.status === 'Rejeitada' ? 'Rejeitado por' : 'Aprovado por'} name={gmud.aprovador_nome} date={gmud.data_aprovacao} />
                            )}
                            {gmud.executado_por_nome && (
                                <PersonRow label="Executado por" name={gmud.executado_por_nome} date={gmud.data_conclusao} />
                            )}
                        </div>
                    </Card>

                    {/* Metadata */}
                    <Card title="Informações do Registro" icon={FaClock}>
                        <div className="gmud-fields-group-sm">
                            <Field label="Criado em" value={formatDate(gmud.criado_em)} />
                            {gmud.data_aprovacao && <Field label="Data de Aprovação" value={formatDate(gmud.data_aprovacao)} />}
                            {gmud.data_conclusao && <Field label="Registro de Conclusão" value={formatDate(gmud.data_conclusao)} />}
                            {gmud.data_real_conclusao && <Field label="Fim Real da Execução" value={formatDate(gmud.data_real_conclusao)} />}
                        </div>
                    </Card>
                </div>
            </div>

            {/* ── Modal ── */}
            {isModalOpen && (
                <VincularModal
                    gmudId={id}
                    onClose={() => setIsModalOpen(false)}
                    onAdded={() => { fetchGmud(); }}
                />
            )}
        </div>
    );
};

const PersonRow = ({ label, name, date }) => (
    <div className="gmud-person-card">
        <div className="gmud-person-avatar">
            {name?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div className="gmud-person-info">
            <span className="gmud-data-label">{label}</span>
            <span className="gmud-person-name">{name}</span>
            {date && <span className="gmud-person-date">{formatDateShort(date)}</span>}
        </div>
    </div>
);

export default GmudDetailPage;